import React, { useEffect, useState, useMemo } from "react";
import { useMsal } from "@azure/msal-react";
import { useNavigate } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";
import { getAuthConfig, getApiBaseUrl } from "../../services/configService";
import "./RulesForm.css";

const NEW_DEFINITION = "__NEW__";

const RulesForm = () => {
  const { instance, accounts } = useMsal();
  const authConfig = getAuthConfig();
  const navigate = useNavigate();

  const [definitions, setDefinitions] = useState([]);
  const [captureRules, setCaptureRules] = useState([]);
  const [mednoteRules, setMednoteRules] = useState([]);
  const [priorities, setPriorities] = useState([]);

  const [selectedDefinition, setSelectedDefinition] = useState(null);
  const [search, setSearch] = useState("");

  const [showDefModal, setShowDefModal] = useState(false);
  const [showRuleModal, setShowRuleModal] = useState(false);

  const [showPriorityModal, setShowPriorityModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [currentPriority, setCurrentPriority] = useState({
    title: "",
    label_name: "",
    color: "#6B7280",
    priority: ""
  });
  
  const [currentRule, setCurrentRule] = useState(null);
  const [ruleType, setRuleType] = useState(null);

  const [formDef, setFormDef] = useState({
    display_title: "",
    title: "",
    description: ""
  });

  const getToken = async () => {
    const res = await instance.acquireTokenSilent({
      account: accounts[0],
      scopes: [`api://${authConfig.clientId}/access_as_user`]
    });
    return res.accessToken;
  };

  /* ======================
     FETCH ALL DATA
  ====================== */
  const fetchAll = async () => {
    const token = await getToken();

    const headers = { Authorization: `Bearer ${token}` };

    const [defs, captures, meds, prios] = await Promise.all([
      fetch(`${getApiBaseUrl()}/admin/definitions`, { headers }),
      fetch(`${getApiBaseUrl()}/admin/capture-rules`, { headers }),
      fetch(`${getApiBaseUrl()}/admin/mednote-rules`, { headers }),
      fetch(`${getApiBaseUrl()}/admin/priorities`, { headers })
    ]);

    setDefinitions(await defs.json());
    setCaptureRules(await captures.json());
    setMednoteRules(await meds.json());
    setPriorities(await prios.json());
  };

  useEffect(() => {
    fetchAll();
  }, []);

  useEffect(() => {
    if (!selectedItem || selectedItem.type !== "definition") return;
  
    const updated = definitions.find(d => d.id === selectedItem.data.id);
  
    if (updated) {
      setSelectedDefinition(updated);
      setSelectedItem({
        type: "definition",
        data: updated
      });
    }
  }, [definitions]);

  const sidebarItems = useMemo(() => {
    const items = [];
  
    // definitions
    definitions.forEach(def => {
        const rules = captureRules.filter(
          r => r.charge_capture_id === def.id
        );
      
        const total = rules.length;
        const active = rules.filter(r => r.is_active).length;
      
        items.push({
          type: "definition",
          id: def.id,
          title: def.display_title,
          subtitle: def.title,
          raw: def,
          ruleStats: {
            active,
            total
          }
        });
      });
  
    // med notes
    mednoteRules.forEach(rule => {
      items.push({
        type: "mednote",
        id: rule.id,
        title: rule.rule_title,
        subtitle: rule.rule_description,
        raw: rule
      });
    });
  
    return items;
  }, [definitions, mednoteRules]);

  const getRuleCounts = (defId) => {
    const rules = captureRules.filter(r => r.charge_capture_id === defId);
  
    const total = rules.length;
    const active = rules.filter(r => r.is_active).length;
  
    return { active, total };
  };

  /* ======================
     FILTERED DEFINITIONS
  ====================== */
  const filteredItems = useMemo(() => {
    const term = search.toLowerCase();
  
    return sidebarItems.filter(item => {
      if (item.type === "definition") {
        const def = item.raw;
  
        // match definition text
        const baseMatch =
          def.title?.toLowerCase().includes(term) ||
          def.display_title?.toLowerCase().includes(term) ||
          def.description?.toLowerCase().includes(term);
  
        if (baseMatch) return true;
  
        // ✅ match associated capture rule values
        return captureRules.some(r =>
          r.charge_capture_id === def.id &&
          (
            r.lookup_value?.toLowerCase().includes(term) ||
            r.charge_value?.toLowerCase().includes(term) ||
            r.exception_value?.toLowerCase().includes(term)
          )
        );
      }
  
      if (item.type === "mednote") {
        const rule = item.raw;
  
        return (
          rule.rule_title?.toLowerCase().includes(term) ||
          rule.rule_description?.toLowerCase().includes(term) ||
          rule.search_term?.toLowerCase().includes(term) ||
          rule.code_list?.toLowerCase().includes(term) ||
          rule.price_code_list?.toLowerCase().includes(term)
        );
      }
  
      return false;
    });
  }, [sidebarItems, search, captureRules]);

  /* ======================
     SELECT DEFINITION
  ====================== */
  const selectDefinition = (def) => {
    setSelectedDefinition(def);
  };

  /* ======================
     RULE FILTERING
  ====================== */
  const relatedCapture = captureRules.filter(
    r => r.charge_capture_id === selectedItem?.data?.id
  );

  /* ======================
     CLEAN HELPERS
  ====================== */
  const cleanCodeList = (val) => {
    const cleaned = (val || "")
      .replace(/\s+/g, "")
      .toUpperCase()
      .replace(/,+/g, ",")
      .replace(/^,|,$/g, "");
  
    return cleaned === "" ? null : cleaned;
  };  

  const toNullIfEmpty = (val) => {
    if (val === undefined || val === null) return null;
    const trimmed = val.toString().trim();

    return trimmed === "" ? null : trimmed;
  };

  /* ======================
     CREATE DEFINITION
  ====================== */
  const createDefinition = async () => {
    const token = await getToken();
  
    const isEdit = !!formDef.id;
  
    const res = await fetch(
      isEdit
        ? `${getApiBaseUrl()}/admin/definitions/${formDef.id}`
        : `${getApiBaseUrl()}/admin/definitions`,
      {
        method: isEdit ? "PATCH" : "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },    
        body: JSON.stringify({
          ...formDef,
          display_title: toNullIfEmpty(formDef.display_title),
          title: toNullIfEmpty(formDef.title),
          description: toNullIfEmpty(formDef.description)
        })
      }
    );
  
    const updatedDef = await res.json();
  
    setShowDefModal(false);   
    setSelectedDefinition(updatedDef);
    setSelectedItem({
      type: "definition",
      data: updatedDef
    });

    fetchAll();
  };

  /* ======================
     SAVE RULE
  ====================== */
const saveRule = async () => {
  const token = await getToken();

  let baseUrl = "";
  let payload = { ...currentRule };
  console.log("Original Payload: ", payload);
  Object.keys(payload).forEach(key => {
    if (typeof payload[key] === "string") {
      payload[key] = toNullIfEmpty(payload[key]);
    }
  });

  if (ruleType === "capture") {
    payload.lookup_value = cleanCodeList(payload.lookup_value);
    payload.charge_value = cleanCodeList(payload.charge_value);
    payload.exception_value = cleanCodeList(payload.exception_value);

    baseUrl = `${getApiBaseUrl()}/admin/capture-rules`;
  } else {
    payload.code_list = cleanCodeList(payload.code_list);
    payload.price_code_list = cleanCodeList(payload.price_code_list);

    baseUrl = `${getApiBaseUrl()}/admin/mednote-rules`;
  }

  const isEdit = !!currentRule.id;

  const url = isEdit
    ? `${baseUrl}/${currentRule.id}`
    : baseUrl;
  
  console.log("Fixed Payload: ", payload);
  await fetch(url, {
    method: isEdit ? "PATCH" : "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json"
    },
    body: JSON.stringify(payload)
  });

  setShowRuleModal(false);
  fetchAll();
};


const savePriority = async () => {
  const token = await getToken();

  const isEdit = !!currentPriority.id;

  const payload = {
    ...currentPriority,
    label_name: toNullIfEmpty(currentPriority.label_name),
    title: toNullIfEmpty(currentPriority.title),
    color: toNullIfEmpty(currentPriority.color)?.toUpperCase(),
    priority: currentPriority.priority || null,
    charge_capture_id:
      selectedItem?.type === "definition"
        ? String(selectedItem.data.id)
        : null
  };

  await fetch(
    isEdit
      ? `${getApiBaseUrl()}/admin/priorities/${currentPriority.id}`
      : `${getApiBaseUrl()}/admin/priorities`,
    {
      method: isEdit ? "PATCH" : "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json"
      },
      body: JSON.stringify(payload)
    }
  );

  setShowPriorityModal(false);
  fetchAll();
};

  const getPriorityKey = (item) => {
    if (!item) return null;
  
    return item.title || item.rule_title || null;
  };
  
  const getPriorityForRule = (item) => {
    if (!item) return null;

    if (item.id !== undefined) {
      return priorities.find(
        p => String(p.charge_capture_id) === String(item.id)
      );
    }

    return priorities.find(
      p => p.title === item.rule_title
    );
  };

  return (
    <div className="rules-page">

      {/* SIDEBAR */}
      <div className="rules-sidebar">

        <div className="rules-sidebar-header">
          <button onClick={() => navigate(-1)} className="back-btn-auth">
            <FaArrowLeft />
          </button>

          <input
            placeholder="Search definitions..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
            <button
                className="new-rule-btn"               
                onClick={() => {
                  setFormDef({
                    display_title: "",
                    title: "",
                    description: ""
                  });
                  setShowDefModal(true);
                }}
                >
                + New Definition
            </button>
            
            <button
                className="new-rule-btn alt"
                onClick={() => {
                  setRuleType("mednote");
                  setCurrentRule({
                    rule_title: "",
                    rule_description: "",
                    search_term: "",
                    code_list: "",
                    price_code_list: "",
                    is_active: true
                  });
                  setShowRuleModal(true);
                }}
                >
                + New Med Note
            </button>
        </div>

        <div className="rules-list">
            {filteredItems.map(item => (
                <div
                    key={`${item.type}-${item.id}`}                    
                    className={`rule-card ${                      
                        selectedItem?.type === item.type &&
                        selectedItem?.data?.id === item.id
                        ? "selected"
                        : ""
                    }`}
                    onClick={() => {

                      setSelectedItem({
                        type: item.type,
                        data: item.raw
                      });
                      
                      if (item.type === "definition") {
                        setSelectedDefinition(item.raw);
                        setCurrentRule(null);
                        setRuleType(null);
                      } else {
                        setSelectedDefinition(null); 
                        setCurrentRule(item.raw);
                        setRuleType("mednote");
                      }
                      
            }}
  >
    <div className="rule-card-title">{item.title}</div>
    <div className="rule-card-sub">{item.subtitle}</div>

    <div className="rule-card-meta-row">

      <div className="rule-type-pill">
        {item.type === "definition" ? "Definition" : "Med Note"}
      </div>

      {(() => {
        const priority = getPriorityForRule(item.raw);

        if (!priority) return null;

        return (
          <div
            className="priority-pill small"
            style={{ background: priority.color }}
          >
            {priority.label_name}
          </div>
        );
      })()}

    </div>
    
    {item.type === "definition" && (
    <div
        className={`rule-count-pill ${
        item.ruleStats.active > 0 ? "has-active" : "none-active"
        }`}
    >
        {item.ruleStats.active}/{item.ruleStats.total}
    </div>
    )}

  </div>
))}
        </div>
      </div>

      {/* MAIN */}
      <div className="rules-main">

        {/* ✅ 1. Definition Selected */}      
          {selectedItem?.type === "definition" && (() => {
            const def = definitions.find(d => d.id === selectedItem.data.id);
            if (!def) return null;

            return (
              <div className="definition-panel">

                {/* ✅ HEADER */}
                <div className="definition-header">

                    <div>

                        <div className="definition-title-row">
                        <h2>{def.display_title}</h2>

                        {(() => {
                            const priority = getPriorityForRule(def);

                            if (!priority) return null;

                            return (
                            <div className="definition-header-pills">
                                <div
                                className="priority-pill"
                                style={{ background: priority.color }}
                                >
                                {priority.label_name} - {priority.priority}
                                </div>
                            </div>
                            );
                        })()}
                        </div>

                        <div className="definition-subtitle">
                        {def.title}
                        </div>

                    </div>

                    <div className="definition-header-actions">
                      <button
                        className="edit-definition-btn"
                        onClick={() => {
                          setFormDef({
                            display_title: def.display_title || "",
                            title: def.title || "",
                            description: def.description || "",
                            id: def.id 
                          });

                          setShowDefModal(true);
                        }}
                      >
                        Edit Definition
                      </button>

                      <button
                        className="priority-btn"                        
                        onClick={() => {
                          const existing = getPriorityForRule(def);

                          if (existing) {
                            setCurrentPriority(existing);
                          } else {
                            setCurrentPriority({
                              title: def.title,
                              label_name: "",
                              color: "#6B7280",
                              priority: "",
                              charge_capture_id: def.id 
                            });
                          }

                          setShowPriorityModal(true);
                        }}
                        >
                        Manage Priority
                      </button>

                      <button
                        className="header-new-rule-btn"
                        onClick={() => {
                            setRuleType("capture");
                            setCurrentRule({ charge_capture_id: def.id });
                            setShowRuleModal(true);
                        }}
                        >
                        + Capture Rule
                      </button>

                    </div>
                </div>

                <div className="definition-description">
                {def.description}
                </div>

                {/* ✅ RULE TABLE */}
                <div className="rules-card-container">

                    {relatedCapture.map((r) => (
                    <div
                        key={r.id}
                        className={`rule-card-large ${!r.is_active ? "inactive" : ""}`}
                    >
                    
                        {/* ✅ NEW STATUS HEADER */}
                        <div
                          className={`rule-card-status-bar ${
                            r.is_active ? "active" : "inactive"
                          }`}
                        >
                          {r.is_active ? "ACTIVE" : "INACTIVE"}
                        </div>

                        {/* ✅ CONTENT */}
                        <div className="rule-card-content">
                    
                        {/* LOOKUP */}
                        <div className="rule-block-first">
                            <div className="rule-label">Lookup</div>
                            <div className="rule-main">
                            <span className="rule-type">{r.lookup_type}</span>
                            <div className="rule-value">{r.lookup_value}</div>
                            </div>
                        </div>
                    
                        {/* CHARGE */}
                        <div className="rule-block">
                            <div className="rule-label">Charge</div>
                            <div className="rule-main">
                            <span className="rule-type">{r.charge_type}</span>
                            <div className="rule-value">{r.charge_value}</div>
                            </div>
                        </div>
                    
                        {/* EXCEPTION */}
                        <div className="rule-block">
                            <div className="rule-label">Exception</div>
                    
                            {(!r.exception_type && !r.exception_logic && !r.exception_value) ? (
                            <div className="rule-empty">No exceptions listed</div>
                            ) : (
                            <div className="rule-main">
                                <span className="rule-type">{r.exception_type || "-"}</span>
                                <div className="rule-meta">{r.exception_logic || "-"}</div>
                                <div className="rule-value">{r.exception_value || "-"}</div>
                            </div>
                            )}
                        </div>
                    
                        {/* PRICE */}
                        <div className="rule-block">
                            <div className="rule-label">Price Lookup</div>
                            <div className="rule-value">{r.price_lookup || "-"}</div>
                        </div>
                    
                        </div>


                        {/* ✅ NEW HEADER ROW */}
                        <div className="rule-card-top">
                    
                        <div className="rule-card-buttons">
                    
                            <button
                            className="edit-btn"
                            onClick={() => {
                                setRuleType("capture");
                                setCurrentRule(r);
                                setShowRuleModal(true);
                            }}
                            >
                            Edit
                            </button>
                    
                            <button
                            className={r.is_active ? "deactivate-btn" : "activate-btn"}
                            onClick={async () => {
                                const token = await getToken();
                    
                                await fetch(
                                `${getApiBaseUrl()}/admin/capture-rules/${r.id}`,
                                {
                                    method: "PATCH",
                                    headers: {
                                    Authorization: `Bearer ${token}`,
                                    "Content-Type": "application/json",
                                    },
                                    body: JSON.stringify({
                                    ...r,
                                    is_active: !r.is_active,
                                    }),
                                }
                                );
                    
                                fetchAll();
                            }}
                            >
                            {r.is_active ? "Deactivate" : "Activate"}
                            </button>
                    
                        </div>
                    
                        </div>
                    
                    </div>
                    ))}

                    </div>
            </div>
            )})()}

        {/* ✅ 2. Med Note Selected */}
        {selectedItem?.type === "mednote" && currentRule && (

          <div className="rules-card-container">

            
          {mednoteRules
            .filter(r => r.id === currentRule?.id)
            .map(r => (
              <div
                key={r.id}
                className={`rule-card-large ${!r.is_active ? "inactive" : ""}`}
              >

                {/* STATUS BAR */}
                <div className={`rule-card-status-bar ${r.is_active ? "active" : "inactive"}`}>
                  {r.is_active ? "ACTIVE" : "INACTIVE"}
                </div>

                {/* CONTENT */}
                <div className="rule-card-content">

                  <div className="rule-block-first">
                    <div className="rule-label">Title</div>
                    <div className="rule-value">{r.rule_title}</div>
                  </div>

                  <div className="rule-block">
                    <div className="rule-label">Description</div>
                    <div className="rule-value">{r.rule_description}</div>
                  </div>

                  <div className="rule-block">
                    <div className="rule-label">Search Terms</div>
                    <div className="rule-value">{r.search_term}</div>
                  </div>

                  <div className="rule-block">
                    <div className="rule-label">Code List</div>
                    <div className="rule-value">{r.code_list}</div>
                  </div>

                  <div className="rule-block">
                    <div className="rule-label">Price Codes</div>
                    <div className="rule-value">{r.price_code_list}</div>
                  </div>

                </div>

                {/* ACTIONS */}
                <div className="rule-card-top">
                  <div className="rule-card-buttons">
                    <button
                      className="priority-btn"                                 
                      onClick={() => {
                        const existing = getPriorityForRule(r);

                        if (existing) {
                          setCurrentPriority(existing);
                        } else {
                          setCurrentPriority({
                            title: r.rule_title,
                            label_name: "",
                            color: "#6B7280",
                            priority: ""
                          });
                        }

                        setShowPriorityModal(true);
                      }}
                      >
                      Manage Priority
                    </button>
                    <button
                      className="edit-btn"
                      onClick={() => {
                        setRuleType("mednote");
                        setCurrentRule(r);
                        setShowRuleModal(true);
                      }}
                    >
                      Edit
                    </button>

                    <button
                      className={r.is_active ? "deactivate-btn" : "activate-btn"}
                      onClick={async () => {
                        const token = await getToken();

                        await fetch(
                          `${getApiBaseUrl()}/admin/mednote-rules/${r.id}`,
                          {
                            method: "PATCH",
                            headers: {
                              Authorization: `Bearer ${token}`,
                              "Content-Type": "application/json",
                            },
                            body: JSON.stringify({
                              ...r,
                              is_active: !r.is_active,
                            }),
                          }
                        );

                        fetchAll();
                      }}
                    >
                      {r.is_active ? "Deactivate" : "Activate"}
                    </button>

                  </div>
                </div>

              </div>
            ))}

          </div>
          )}

        {/* ✅ 3. Empty State */}
        {!selectedItem && !currentRule && (
        <div className="empty-state">
            Select a definition or med note to begin
        </div>
        )}

      </div>

      {/* CREATE DEF MODAL */}
      {showDefModal && (
        <div className="modal">
          <div className="rule-modal-box modal-definition">

            <div className="modal-header">
              <h3>{formDef.id ? "Edit Definition" : "New Definition"}</h3>
            </div>

            <div className="modal-form">

              <div className="form-group">
                <label>Display Title</label>
                
                <input
                  value={formDef.display_title || ""}
                  onChange={e =>
                    setFormDef({ ...formDef, display_title: e.target.value })
                  }
                />
              </div>

              <div className="form-group">
                <label>Internal Title</label>                
                <input
                  value={formDef.title || ""}
                  onChange={e =>
                    setFormDef({ ...formDef, title: e.target.value })
                  }
                />
              </div>

              <div className="form-group">
                <label>Description</label>                
                <textarea
                  value={formDef.description || ""}
                  onChange={e =>
                    setFormDef({ ...formDef, description: e.target.value })
                  }
                />
              </div>

            </div>

            <div className="modal-actions">
              <button className="cancel-btn" onClick={() => setShowDefModal(false)}>
                Cancel
              </button>
              
              <button className="save-btn" onClick={createDefinition}>
                {formDef.id ? "Save Changes" : "Create"}
              </button>

            </div>

          </div>
        </div>
      )}


      {/* RULE MODAL */}
      {showRuleModal && (
        <div className="modal">
          <div className="rule-modal-box modal-rule">

            <div className="modal-header">
              <h3>{currentRule?.id ? "Edit Rule" : "New Rule"}</h3>
            </div>

            <div className="modal-form">

              {/* LOOKUP */}
              {ruleType === "capture" && (
                <>
                  <div className="form-group">
                    <label>Lookup Type</label>
                    <select
                      value={currentRule.lookup_type || ""}
                      onChange={e =>
                        setCurrentRule({
                          ...currentRule,
                          lookup_type: e.target.value,
                          lookup_value: ""
                        })
                      }
                    >
                      <option value="">None</option>
                      <option value="Item Code">Item Code</option>
                      <option value="Class Code">Class Code</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Lookup Value</label>
                    <input
                      disabled={!currentRule.lookup_type}
                      value={currentRule.lookup_value || ""}
                      placeholder="ABC123,XYZ456"
                      onChange={e =>
                        setCurrentRule({
                          ...currentRule,
                          lookup_value: e.target.value.toUpperCase()
                        })
                      }
                    />
                  </div>

                  {/* CHARGE */}
                  <div className="form-group">
                    <label>Charge Type</label>
                    <select
                      value={currentRule.charge_type || ""}
                      onChange={e =>
                        setCurrentRule({
                          ...currentRule,
                          charge_type: e.target.value,
                          charge_value: ""
                        })
                      }
                    >
                      <option value="">None</option>
                      <option value="Item Code">Item Code</option>
                      <option value="Class Code">Class Code</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Charge Value</label>
                    <input
                      disabled={!currentRule.charge_type}
                      value={currentRule.charge_value || ""}
                      onChange={e =>
                        setCurrentRule({
                          ...currentRule,
                          charge_value: e.target.value.toUpperCase()
                        })
                      }
                    />
                  </div>

                  {/* EXCEPTION */}
                  <div className="form-group">
                    <label>Exception Type</label>
                    <select
                      value={currentRule.exception_type || ""}
                      onChange={e =>
                        setCurrentRule({
                          ...currentRule,
                          exception_type: e.target.value,
                          exception_logic: ""
                        })
                      }
                    >
                      <option value="">None</option>
                      <option value="Item Code">Item Code</option>
                      <option value="Class Code">Class Code</option>
                    </select>
                  </div>

                  {currentRule.exception_type && (
                    <>
                      <div className="form-group">
                        <label>Exception Logic</label>
                        <select
                          value={currentRule.exception_logic || ""}
                          onChange={e =>
                            setCurrentRule({
                              ...currentRule,
                              exception_logic: e.target.value
                            })
                          }
                        >
                          <option value="">Select Logic</option>
                          <option>Equal Quantity</option>
                          <option>Must Include Exception</option>
                          <option>Should Not Include Exception</option>
                        </select>
                      </div>

                      <div className="form-group">
                        <label>Exception Values</label>
                        <input
                          value={currentRule.exception_value || ""}
                          onChange={e =>
                            setCurrentRule({
                              ...currentRule,
                              exception_value: e.target.value.toUpperCase()
                            })
                          }
                        />
                      </div>
                    </>
                  )}

                  {/* PRICE */}
                  <div className="form-group">
                    <label>Price Lookup</label>
                    <input
                      value={currentRule.price_lookup || ""}
                      onChange={e =>
                        setCurrentRule({
                          ...currentRule,
                          price_lookup: e.target.value.toUpperCase()
                        })
                      }
                    />
                  </div>
                </>
              )}

              {ruleType === "mednote" && (
                <>
                  <div className="form-group">
                    <label>Rule Title</label>
                    <input
                      value={currentRule.rule_title || ""}
                      onChange={e =>
                        setCurrentRule({ ...currentRule, rule_title: e.target.value })
                      }
                    />
                  </div>

                  <div className="form-group">
                    <label>Description</label>
                    <textarea
                      value={currentRule.rule_description || ""}
                      onChange={e =>
                        setCurrentRule({ ...currentRule, rule_description: e.target.value })
                      }
                    />
                  </div>

                  <div className="form-group">
                    <label>Search Terms</label>
                    <input
                      value={currentRule.search_term || ""}
                      onChange={e =>
                        setCurrentRule({ ...currentRule, search_term: e.target.value })
                      }
                    />
                  </div>

                  <div className="form-group">
                    <label>Code List</label>
                    <input
                      value={currentRule.code_list || ""}
                      onChange={e =>
                        setCurrentRule({
                          ...currentRule,
                          code_list: e.target.value.toUpperCase().replace(/\s+/g, "")
                        })
                      }
                    />
                  </div>

                  <div className="form-group">
                    <label>Price Code List</label>
                    <input
                      value={currentRule.price_code_list || ""}
                      onChange={e =>
                        setCurrentRule({
                          ...currentRule,
                          price_code_list: e.target.value.toUpperCase().replace(/\s+/g, "")
                        })
                      }
                    />
                  </div>
                </>
              )}              

            </div>

            <div className="modal-actions">
              <button className="cancel-btn" onClick={() => setShowRuleModal(false)}>
                Cancel
              </button>

              <button className="save-btn" onClick={saveRule}>
                Save
              </button>
            </div>

          </div>
        </div>
      )}

      {showPriorityModal && (
        <div className="modal">

            <div
            className="priority-modal"
            style={{ borderLeft: `6px solid ${currentPriority.color || "#6B7280"}` }}
            >

            <h3>
                {currentPriority.id ? "Edit Priority" : "New Priority"}
            </h3>

            <div className="priority-form">

                {/* LABEL */}
                <div className="priority-form-group">
                <label>Label (max 12)</label>
                <input
                    maxLength={12}
                    value={currentPriority.label_name || ""}
                    onChange={(e) =>
                    setCurrentPriority({
                        ...currentPriority,
                        label_name: e.target.value
                    })
                    }
                />
                </div>

                {/* COLOR */}
                <div className="priority-form-group">
                <label>Color</label>
                <div className="color-row">
                    <input
                    type="color"
                    value={currentPriority.color || "#6B7280"}
                    onChange={(e) =>
                        setCurrentPriority({
                        ...currentPriority,
                        color: e.target.value.toUpperCase()
                        })
                    }
                    />

                    <input
                    className="color-input"
                    value={currentPriority.color || ""}
                    onChange={(e) =>
                        setCurrentPriority({
                        ...currentPriority,
                        color: e.target.value.toUpperCase()
                        })
                    }
                    />
                </div>
                </div>

                {/* PRIORITY */}
                <div className="priority-form-group">
                <label>Priority (number)</label>
                <input
                    type="number"
                    value={currentPriority.priority || ""}
                    onChange={(e) =>
                    setCurrentPriority({
                        ...currentPriority,
                        priority: parseInt(e.target.value || 0)
                    })
                    }
                />
                </div>

            </div>

            <div className="modal-actions">
                <button className="cancel-btn" onClick={() => setShowPriorityModal(false)}>
                Cancel
                </button>

                <button className="save-btn" onClick={savePriority}>
                Save
                </button>
            </div>

            </div>

        </div>
      )}
    </div>
  );
};

export default RulesForm;